import { Suspense } from 'react';
import { NavLink, Outlet, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import * as Sentry from '@sentry/react';
import { Spinner } from '@shared/components/ui/Spinner';
import { AppErrorFallback } from '@shared/components/ui/AppErrorFallback';
import { cn } from '@shared/utils/utils';

/**
 * Fallback de carga bajo las pestañas: el Shell permanece montado al cambiar
 * Activos ↔ Historial; solo el `<Outlet />` suspende.
 */
function MerchandiseBlocksTabSuspenseFallback() {
  return (
    <div
      className="flex min-h-[24rem] flex-1 flex-col items-center justify-center"
      data-testid="merchandise-blocks-tab-suspense"
    >
      <Spinner size="md" />
    </div>
  );
}

/**
 * Shell de pestañas como layout route (`Outlet`).
 * Las tabs no se desmontan al cambiar Activos/Historial; el spinner vive abajo.
 */
export function MerchandiseBlocksShell() {
  const { t } = useTranslation('merchandiseBlocksModule');
  const { pathname } = useLocation();

  return (
    <div className="flex h-full min-h-0 w-full min-w-0 flex-1 flex-col" data-testid="merchandise-blocks-shell">
      {/* Pestañas Activos / Historial — mismo diseño que /incidents (sin contadores) */}
      <div className="shrink-0 px-4 pt-4">
        <nav
          className="mb-0 flex overflow-x-auto rounded-lg bg-card-bg p-1 text-sm light:border light:border-card-border scrollbar-hide"
          style={{ WebkitOverflowScrolling: 'touch' }}
          role="tablist"
          aria-label={t('shell.tabs_label')}
          data-testid="merchandise-blocks-tabs"
        >
          <NavLink
            to="/merchandise-blocks"
            end
            role="tab"
            data-testid="merchandise-blocks-tab-alive"
            className={({ isActive }) =>
              cn(
                'flex items-center gap-1.5 whitespace-nowrap rounded px-3 py-1 hover:bg-hover-press',
                isActive ? 'bg-hover-press' : '',
              )
            }
          >
            {t('shell.tabs.alive')}
          </NavLink>
          <NavLink
            to="/merchandise-blocks/history"
            role="tab"
            data-testid="merchandise-blocks-tab-history"
            className={({ isActive }) =>
              cn(
                'flex items-center gap-1.5 whitespace-nowrap rounded px-3 py-1 hover:bg-hover-press',
                isActive ? 'bg-hover-press' : '',
              )
            }
          >
            {t('shell.tabs.history')}
          </NavLink>
        </nav>
      </div>
      {/* Contenido de pestaña: frontera local para no blanquear las tabs */}
      <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-y-auto" data-testid="merchandise-blocks-tab-scroll">
        <Sentry.ErrorBoundary key={pathname} fallback={<AppErrorFallback variant="inline" />}>
          <Suspense fallback={<MerchandiseBlocksTabSuspenseFallback />}>
            <Outlet />
          </Suspense>
        </Sentry.ErrorBoundary>
      </div>
    </div>
  );
}
